import { useIntl } from 'react-intl';

import { adoptOrphans, FocusRingVariants, FontWeights } from '@calm-web/design-system';

import { useAnalytics } from '@/hooks/analytics';
import { useCoupon } from '@/hooks/purchase';
import { useUserEligibility } from '@/hooks/user/useUserEligibility';
import { useAmplitudeExperiment } from '@/hooks/utils/experiments/useAmplitudeExperiment';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import messages from '@/messages/messages';
import { AmplitudeExperimentNames } from '@/utils/experiments/amplitudeExperiment';

import { Wrapper } from './styles';

const HeaderButtonCta = ({
	isOpaque,
	isMobileMenuOpen,
}: {
	isOpaque: boolean;
	isMobileMenuOpen: boolean;
}) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const routerPush = useRouterPush();
	const { isCoupon } = useCoupon();
	const { isLoggedOut, isLoggedInEligibleForTrial, isLoggedInEligibleToSubscribe, isLoggedInValid } =
		useUserEligibility();
	const { isEnrolled: isFTUESignupEnrolled } = useAmplitudeExperiment('www-skip-questionnaire');

	const { isEnrolled: isRunwayerQuizEnrolled } = useAmplitudeExperiment(
		AmplitudeExperimentNames.RUNWAYER_QUIZ_ON_CALM_HOME_PAGE,
	);

	const buttonTextColor = isOpaque ? 'white' : 'gray7';
	const buttonBgColor = isOpaque ? 'buttonPrimaryBg' : 'white';

	const getButtonProperties = (): [
		href:
			| {
					type: 'internal';
					pathname: string;
					queryParams?: Record<string, string | null | undefined>;
			  }
			| { type: 'external'; url: string },
		label: string,
	] => {
		if (isLoggedInValid) {
			return [{ type: 'internal', pathname: '/app' }, formatMessage(messages.continueToApp)];
		}

		if (isLoggedInEligibleToSubscribe || isCoupon) {
			return [{ type: 'internal', pathname: '/subscribe' }, formatMessage(messages.subscribeToCalm)];
		}

		if (isRunwayerQuizEnrolled) {
			const url = new URL('https://quiz.calm.com/calm-ce4a1');
			return [{ type: 'external', url: url.toString() }, formatMessage(messages.tryCalm)];
		}

		return [
			{
				type: 'internal',
				pathname: '/signup-flow',
				queryParams: {
					source: 'homepage',
					...(isFTUESignupEnrolled && { utm_content: 'organic_ft-sf' }),
				},
			},
			formatMessage(messages.tryCalm),
		];
	};

	const [href, label] = getButtonProperties();

	const onFreeTrialClick = async () => {
		logEvent({
			eventName: 'Homepage : NavHeader : Try Calm for Free : Clicked',
		});

		if (isLoggedOut || isLoggedInEligibleForTrial) {
			logEvent({
				eventName: 'Landing Page : Signup Flow CTA : Clicked',
			});
		}

		if (href.type === 'external') {
			window.location.assign(href.url);
		} else {
			routerPush(href.pathname, href.queryParams);
		}
	};

	return (
		<Wrapper
			tabIndex={isMobileMenuOpen ? -1 : 0}
			fullWidth
			fontWeight={FontWeights.Demi}
			backgroundColor={buttonBgColor}
			textColor={buttonTextColor}
			onPress={onFreeTrialClick}
			focusRingVariant={isOpaque ? FocusRingVariants.Dark : FocusRingVariants.Light}
		>
			{adoptOrphans(label, 3)}
		</Wrapper>
	);
};

export default HeaderButtonCta;
